import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ServiceDetailTemplate from "@/pages/ServiceDetailTemplate";
import { CATEGORY_CONFIG } from "@/lib/categories";

const normalize = (row) => ({
  slug: row.slug,
  category: row.category,
  subCategory: row.sub_category,
  icon: row.icon,
  image: row.image,
  title: row.title,
  subtitle: row.subtitle,
  readTime: row.read_time,
  intro: row.intro,
  ...row.sections,
});

export default function CategoryServiceDetail({ categorySlug }) {
  const { slug } = useParams();
  const config = CATEGORY_CONFIG[categorySlug];
  const { isAdmin } = useAuth();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!config) return;
    setLoading(true);
    setNotFound(false);
    Promise.all([api.getService(slug), api.listServices(config.dbCategory)])
      .then(([{ service }, { services }]) => {
        if (service.category !== config.dbCategory) throw new Error("Category mismatch");
        setService(normalize(service));
        setAllServices(services);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [config, slug]);

  if (!config || notFound) return <Navigate to={config ? `/${categorySlug}` : "/services"} replace />;

  const contactHref = `/contact?service=${encodeURIComponent(config.contactService)}`;
  const uploadHref = service
    ? `/upload?service=${encodeURIComponent(service.slug)}&category=${encodeURIComponent(config.dbCategory)}&title=${encodeURIComponent(service.title)}`
    : "/upload";
  const relatedItems = service
    ? (service.related || [])
        .map((relSlug) => allServices.find((s) => s.slug === relSlug))
        .filter(Boolean)
        .map((r) => ({ slug: r.slug, title: r.title, icon: r.icon, href: `/${categorySlug}/${r.slug}` }))
    : [];

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="category-service-page">
      <ScrollProgress />
      <Navbar />
      <main>
        {loading || !service ? (
          <div className="pt-40 pb-20 text-center text-slate-500">Loading...</div>
        ) : (
          <ServiceDetailTemplate
            service={service}
            accentKey={config.accentKey}
            breadcrumbs={[
              ...(config.parent ? [config.parent] : []),
              { label: config.label, href: `/${categorySlug}` },
            ]}
            contactHref={contactHref}
            uploadHref={uploadHref}
            showUpload={!isAdmin}
            relatedItems={relatedItems}
          />
        )}
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
